import { useSyncExternalStore } from "react";
import type { DraftLessonResponse, LessonDraft } from "@compliance-sage/contracts";
import { api, ApiError } from "../../api/client";

export const LESSON_DRAFT_JOB_EVENT = "compliance-sage:lesson-draft-job";

export interface LessonDraftFiles {
  originalFile: File | null;
  feedbackFile: File | null;
  modifiedFile: File | null;
}

export interface LessonDraftForm {
  originalText: string;
  modifiedText: string;
  feedbackComments: string;
  files: LessonDraftFiles;
}

type IdleJob = { status: "idle" };

interface JobBase {
  tempId: string;
  startedAt: number;
  form: LessonDraftForm;
}

export type DraftingJob = JobBase & { status: "drafting" };

export type ReadyJob = JobBase & {
  status: "ready";
  result: DraftLessonResponse;
  draft: LessonDraft;
  feedbackComments: string;
};

export type ErrorJob = JobBase & {
  status: "error";
  message: string;
};

export type LessonDraftJobState = IdleJob | DraftingJob | ReadyJob | ErrorJob;

const IDLE: IdleJob = { status: "idle" };

let state: LessonDraftJobState = IDLE;
const listeners = new Set<() => void>();

function emit(next: LessonDraftJobState) {
  state = next;
  listeners.forEach((listener) => listener());
  if (typeof window !== "undefined") {
    window.dispatchEvent(new Event(LESSON_DRAFT_JOB_EVENT));
  }
}

export function getLessonDraftJob(): LessonDraftJobState {
  return state;
}

export function subscribeLessonDraftJob(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useLessonDraftJob() {
  return useSyncExternalStore(subscribeLessonDraftJob, getLessonDraftJob, getLessonDraftJob);
}

export function clearLessonDraftJob() {
  emit(IDLE);
}

export function updateReadyLessonDraft(draft: LessonDraft) {
  if (state.status !== "ready") return;
  emit({ ...state, draft });
}

export async function startLessonDraft(form: LessonDraftForm, fallbackError: string) {
  if (state.status === "drafting") return;

  const tempId = `lesson-draft-${crypto.randomUUID()}`;
  const startedAt = Date.now();
  emit({ status: "drafting", tempId, startedAt, form });

  try {
    const result = await api.draftLesson(
      {
        originalText: form.originalText.trim() || undefined,
        modifiedText: form.modifiedText.trim() || undefined,
        feedbackComments: form.feedbackComments.trim() || undefined,
      },
      form.files,
    );
    if (state.status === "idle" || state.tempId !== tempId) return;
    emit({
      status: "ready",
      tempId,
      startedAt,
      form,
      result,
      draft: result.draft,
      feedbackComments: result.feedbackComments,
    });
  } catch (err) {
    if (state.status === "idle" || state.tempId !== tempId) return;
    emit({
      status: "error",
      tempId,
      startedAt,
      form,
      message: err instanceof ApiError ? err.message : fallbackError,
    });
  }
}
